<script setup lang="ts">
/**
 * AppShell — top bar, primary nav rail, content slot (Story 301, restyled
 * on `@design-system/vue-ui` primitives in 016.4).
 *
 * The shell is route-agnostic: it renders the top bar with the brand mark
 * and the design system's `ThemeToggle`, a left rail with primary
 * navigation, and a default slot for the active route. Routes with
 * `meta.chromeless` (016.4 AC4) render without the nav rail via the
 * `chromeless` prop, keeping only the brand chrome.
 */
import { watch } from 'vue';
import { ThemeToggle, useTheme } from '@design-system/vue-ui';
import { applyTheme, resolveThemeName, writeStoredTheme } from '../composables/useTheme';

defineProps<{
  navItems?: ReadonlyArray<{ to: string; label: string; icon?: string }>;
  chromeless?: boolean;
}>();

// `ThemeToggle` is fully self-contained: it drives its own `useTheme()`
// (a VueUse `useColorMode`) and owns the `.dark` class on <html>. It knows
// nothing about this app's legacy `data-theme` attribute that `--gl-*`
// tokens key off, so every change is bridged into the app's own storage/
// attribute here (016.4 AC2, ground truth: `themeBridge.test.ts`).
//
// `useTheme()`'s value is typed `BasicColorSchema` ('auto' | 'light' |
// 'dark'), a wider type than the legacy bridge's binary `ThemeName` — resolve
// 'auto' to the OS preference before bridging (016.4 typecheck fix).
const designSystemTheme = useTheme();
watch(
  designSystemTheme,
  (next) => {
    const resolved = resolveThemeName(next);
    applyTheme(resolved);
    writeStoredTheme(globalThis.localStorage, resolved);
  },
  { immediate: true },
);
</script>

<template>
  <div class="cwc-shell">
    <header class="cwc-shell__topbar" role="banner">
      <div class="cwc-shell__brand">
        <span class="cwc-shell__brand-mark" aria-hidden="true">CWC</span>
        <span class="cwc-shell__brand-text">Connect with Claude</span>
      </div>
      <div class="cwc-shell__topbar-actions">
        <ThemeToggle />
      </div>
    </header>
    <div class="cwc-shell__body" :class="{ 'cwc-shell__body--chromeless': chromeless }">
      <nav v-if="!chromeless" class="cwc-shell__rail" aria-label="Primary">
        <ul class="cwc-shell__rail-list">
          <li v-for="item in (navItems ?? [])" :key="item.to" class="cwc-shell__rail-item">
            <a :href="item.to" class="cwc-shell__rail-link">{{ item.label }}</a>
          </li>
        </ul>
      </nav>
      <main class="cwc-shell__main" role="main">
        <slot />
      </main>
    </div>
  </div>
</template>

<style scoped>
.cwc-shell {
  display: grid;
  grid-template-rows: auto 1fr;
  min-height: 100vh;
  background: var(--background);
  color: var(--foreground);
}

.cwc-shell__topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: calc(var(--spacing) * 3) calc(var(--spacing) * 6);
  background: var(--card);
  border-bottom: 1px solid var(--border);
}

.cwc-shell__brand {
  display: flex;
  align-items: center;
  gap: calc(var(--spacing) * 3);
}

.cwc-shell__brand-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: calc(var(--spacing) * 8);
  height: calc(var(--spacing) * 8);
  background: var(--primary);
  color: var(--primary-foreground);
  border-radius: calc(var(--radius) - 2px);
  font-weight: var(--font-weight-semibold);
  font-size: var(--text-sm);
}

.cwc-shell__brand-text {
  font-weight: var(--font-weight-semibold);
  font-size: var(--text-lg);
}

.cwc-shell__body {
  display: grid;
  grid-template-columns: 240px 1fr;
}

.cwc-shell__body--chromeless {
  grid-template-columns: 1fr;
}

.cwc-shell__rail {
  background: var(--sidebar);
  border-right: 1px solid var(--sidebar-border);
  padding: calc(var(--spacing) * 4);
}

.cwc-shell__rail-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--spacing);
}

.cwc-shell__rail-link {
  display: block;
  padding: calc(var(--spacing) * 2) calc(var(--spacing) * 3);
  border-radius: calc(var(--radius) - 2px);
  color: var(--sidebar-foreground);
  text-decoration: none;
}

.cwc-shell__rail-link:hover {
  background: var(--sidebar-accent);
  color: var(--sidebar-accent-foreground);
}

.cwc-shell__main {
  padding: calc(var(--spacing) * 6);
}

@media (max-width: 720px) {
  .cwc-shell__body {
    grid-template-columns: 1fr;
  }
  .cwc-shell__rail {
    border-right: none;
    border-bottom: 1px solid var(--sidebar-border);
  }
}
</style>
